import * as Sentry from '@sentry/react';
import { facingModeChanged, mediaDevicesUpdated } from 'features/user-media/userMediaSlice';
import {
  enumerateDevices,
  getDevicePermissions,
  stopStreamTracks,
} from 'features/user-media/utils';
import { RefObject } from 'react';
import { store } from 'store/store';
import { logger } from 'utils/logger';
import { BasePublishing } from 'utils/webrtc/publishing';
import { LocalFeedStream, StreamKind } from 'utils/webrtc/types';
import { isDomException } from 'utils/types';
import { getUserMedia } from 'features/user-media/utils/getUserMedia';
import webrtcAdapter from 'webrtc-adapter';
import {
  EnumeratedMediaDevices,
  PermissionStatus,
  UnwrappedMediaDeviceInfo,
} from 'features/user-media/types';
import { selectActiveVirtualBackground } from 'features/virtual-backgrounds/selectors';
import {
  emitPublishEvent,
  PUBLISH_TRACK_ENDED_EVENT,
  PublishTrackEndedOutcome,
  publishTrackEndedMessage,
  PUBLISH_TRACK_MUTED_EVENT,
  PUBLISH_TRACK_UNMUTED_EVENT,
  publishTrackMutedMessage,
  publishTrackUnmutedMessage,
} from 'utils/webrtc/publishing/publishTelemetry';

type LocalTrackRemovalReason = 'ended';

const DEFAULT_CAPTURE_DEVICE_IDS = ['default', 'communications'];

const describeTrack = (track: MediaStreamTrack, reason?: LocalTrackRemovalReason) => ({
  id: track.id,
  kind: track.kind,
  label: track.label,
  readyState: track.readyState,
  muted: track.muted,
  reason,
});

export class MediaController {
  streams: Record<string, LocalFeedStream> = {};

  storedMediaStreams: Record<string, MediaStream> = {};

  tracks: MediaStreamTrack[] = [];

  private captures: Record<
    string,
    { addedAt: number; deviceId?: string; stopReportingMute?: () => void }
  > = {};

  feed: BasePublishing;

  constructor(feed: BasePublishing) {
    this.feed = feed;
  }

  addStream = (track: MediaStreamTrack) => {
    const { id } = track;

    if (this.streams[id]) {
      return;
    }

    // getSettings() on an ended track can be empty, so the identity is captured while it is live.
    const { deviceId, groupId } = track.getSettings();
    const addedAt = performance.now();
    this.captures[id] = {
      addedAt,
      deviceId,
      stopReportingMute:
        this.feed.kind === 'publishing' ? this.reportMuteChanges(track, addedAt) : undefined,
    };

    const message = 'Local track added';
    const payload = { ...describeTrack(track), deviceId, groupId };
    Sentry.addBreadcrumb({
      category: 'streaming',
      message,
      data: payload,
    });

    logger.remote().info(message, payload);

    const clonedTrack = track.clone();
    const mediaStream = new MediaStream();
    mediaStream.addTrack(clonedTrack);

    this.streams[id] = {
      mediaStream,
      id,
      attached: false,
      kind: track.kind as LocalFeedStream['kind'],
    };

    this.tracks.push(track);
  };

  stopStreamTracks = (stream: MediaStream) => {
    const tracks = stream.getTracks();

    tracks.forEach((track) => {
      track.stop();
    });
  };

  removeStream = (id: string) => {
    const stream = this.streams[id];

    if (stream) {
      this.stopStreamTracks(stream.mediaStream);
    }

    this.tracks = this.tracks.filter((track) => track.id !== id);

    delete this.streams[id];
    this.captures[id]?.stopReportingMute?.();
    delete this.captures[id];
  };

  // WebKit reports a capture unit that cannot start, or is interrupted, only as a source mute.
  private reportMuteChanges = (track: MediaStreamTrack, addedAt: number) => {
    const kind = track.kind as StreamKind;
    const report = (muted: boolean, atAdd?: true) =>
      emitPublishEvent({
        event: muted ? PUBLISH_TRACK_MUTED_EVENT : PUBLISH_TRACK_UNMUTED_EVENT,
        message: muted ? publishTrackMutedMessage(kind) : publishTrackUnmutedMessage(kind),
        level: 'info',
        feedType: this.feed.kind,
        attributes: {
          kind,
          trackAgeMs: Math.round(performance.now() - addedAt),
          visibilityState: document.visibilityState,
          atAdd,
        },
      });
    const onMuteChange = (event: Event) => report(event.type === 'mute');

    // A track that is already muted never fires `mute`, and that is the stuck state itself.
    if (track.muted) {
      report(true, true);
    }
    track.addEventListener('mute', onMuteChange);
    track.addEventListener('unmute', onMuteChange);

    return () => {
      track.removeEventListener('mute', onMuteChange);
      track.removeEventListener('unmute', onMuteChange);
    };
  };

  clearStoredMediaStreams = () => {
    Object.keys(this.storedMediaStreams).forEach((id) => {
      this.stopStreamTracks(this.storedMediaStreams[id]);

      delete this.storedMediaStreams[id];
    });
  };

  handleRemoveTrack = async (track: MediaStreamTrack, reason?: LocalTrackRemovalReason) => {
    const message = 'Local track removed';
    const payload = describeTrack(track, reason);
    Sentry.addBreadcrumb({
      category: 'streaming',
      message,
      data: payload,
    });

    logger.remote().info(message, payload);

    const updatedDevices = await enumerateDevices();

    const kind = track.kind as StreamKind;
    const mediaDeviceKind = `${kind}input` as const;

    const permissions = getDevicePermissions(updatedDevices, mediaDeviceKind);
    let blocked = false;
    let settledPermission = { permission: permissions, devices: updatedDevices };

    // block streaming if track removed because of permissions change
    if (permissions !== 'granted') {
      const blockDevices = async (devices: EnumeratedMediaDevices = updatedDevices) => {
        blocked = true;

        logger
          .remote({ capture: 'streaming' })
          .log('Permissions have been changed and not granted anymore, stopping stream', kind);

        store.dispatch(
          mediaDevicesUpdated(devices, {
            updateActiveDevices: true,
            updatePermissions: true,
          })
        );

        if (kind === 'audio') {
          await this.feed.blockAudioPublishing();
        } else {
          await this.feed.blockVideoPublishing();
        }
      };

      if (webrtcAdapter.browserDetails.browser === 'firefox') {
        try {
          const stream = await this.requestExternalStream({
            video: kind === 'video',
            audio: kind === 'audio',
          });
          const checkupDevices = await enumerateDevices();

          const finalPermissions = getDevicePermissions(checkupDevices, mediaDeviceKind);
          settledPermission = { permission: finalPermissions, devices: checkupDevices };

          stopStreamTracks(stream);

          if (finalPermissions !== 'granted') {
            await blockDevices(checkupDevices);
          } else {
            store.dispatch(
              mediaDevicesUpdated(checkupDevices, {
                updateActiveDevices: true,
                updatePermissions: true,
              })
            );
          }
        } catch {
          await blockDevices();
        }
      } else {
        await blockDevices();
      }
    }

    // ScreensharingFeed owns its display track's end (the browser's "Stop sharing" bar).
    if (reason === 'ended' && this.feed.kind === 'publishing') {
      const outcome = blocked ? 'blocked' : await this.turnOffEndedMedium(track, kind);

      this.reportEndedTrack(
        track,
        kind,
        outcome,
        settledPermission.permission,
        settledPermission.devices[mediaDeviceKind]
      );
    }

    this.removeStream(track.id);

    this.feed.updateRedux();
  };

  // The browser ended a live capture: switch the medium off the way a device unplug does, so the UI
  // and remote participants stop showing a mic or camera that sends nothing.
  private turnOffEndedMedium = async (
    track: MediaStreamTrack,
    kind: StreamKind
  ): Promise<PublishTrackEndedOutcome> => {
    // janus leaves onended on tracks it replaced; a stale one ending must not switch off a live medium.
    const isSenderTrack =
      this.feed
        .getPeerConnection()
        ?.getSenders()
        .some((sender) => sender.track === track) ?? false;

    if (!isSenderTrack) {
      return 'skipped-not-current';
    }

    if (!this.feed.mediaStates[kind].broadcasting) {
      return 'skipped-not-broadcasting';
    }

    if (kind === 'audio') {
      await this.feed.stopAudioBroadcasting(true);
    } else {
      await this.feed.stopVideoBroadcasting();
    }

    return 'medium-off';
  };

  private reportEndedTrack = (
    track: MediaStreamTrack,
    kind: StreamKind,
    outcome: PublishTrackEndedOutcome,
    permission: PermissionStatus,
    devices: UnwrappedMediaDeviceInfo[]
  ) => {
    const capture = this.captures[track.id];
    const deviceId = capture?.deviceId;

    emitPublishEvent({
      event: PUBLISH_TRACK_ENDED_EVENT,
      message: publishTrackEndedMessage(kind),
      level: outcome === 'medium-off' || outcome === 'blocked' ? 'warn' : 'info',
      feedType: this.feed.kind,
      attributes: {
        kind,
        outcome,
        mutedAtEnd: track.muted,
        trackAgeMs: capture ? Math.round(performance.now() - capture.addedAt) : undefined,
        visibilityState: document.visibilityState,
        permission,
        deviceStillEnumerated: deviceId
          ? devices.some((device) => device.deviceId === deviceId)
          : undefined,
        capturedViaDefault: deviceId ? DEFAULT_CAPTURE_DEVICE_IDS.includes(deviceId) : undefined,
        vbSelected:
          kind === 'video' ? !!selectActiveVirtualBackground(store.getState()).value : undefined,
        videoPipOpen: document.pictureInPictureElement != null,
      },
    });
  };

  attachToElement = async (id: string, ref: RefObject<HTMLVideoElement>) => {
    if (!ref.current) {
      return;
    }

    try {
      const stream = this.streams[id];

      if (!stream) {
        logger.remote().warn(`Attempted to attach to non-existent stream: ${id}`);
        return;
      }

      if (stream.kind === 'video') {
        const track = stream.mediaStream.getVideoTracks()[0];

        if (track && ref.current && this.feed.kind === 'publishing') {
          const settings = track.getSettings();

          if (settings.facingMode) {
            store.dispatch(facingModeChanged(settings.facingMode));
          }
        }
      }

      ref.current.srcObject = stream.mediaStream;
      await ref.current.play();
    } catch (error) {
      if (!isDomException(error)) {
        Sentry.captureException(error);
        logger.remote().error('An error occurred while attaching a media element', error);
      }
    }
  };

  requestExternalStream = async (constraints?: MediaStreamConstraints) => {
    if (constraints) {
      return getUserMedia(constraints);
    }

    const activeMediaConfig = this.feed.getActiveMediaConfig();

    return getUserMedia({
      audio: activeMediaConfig.audio,
      video: activeMediaConfig.video,
    });
  };
}
